import * as I from "immutable";
import { createSelector } from "reselect";

import { Action, actionCreators } from "../actions";
import { Data } from "../service/Data";
import { notUndefined } from "../utils/any";
import { mapUpdateExisting } from "../utils/immutable";

import { RoomView } from "./rooms";

type viewId = string;
type tag = string;
export interface UploadInProgress {
    tag: string;
    state: "uploading" | "abort";
    progress: number; // 0..1
    file: Blob;
}

export interface Uploaded {
    tag: string;
    state: "uploaded";
    file: Blob;
    data: Data.UploadData;
}

export type Uploading = Uploaded | UploadInProgress;
type UploadingByTag = I.Map<tag, Uploading>;

const initialStore = I.Record({
    uploadingByRoomId: I.Map<viewId, UploadingByTag>(),
})();
type Store = typeof initialStore;

export function update(store = initialStore, action: Action): Store {
    switch (action.type) {
        case actionCreators.onLogout.type:
        case actionCreators.onWrongToken.type: {
            return initialStore;
        }

        case actionCreators.uploadFiles.type: {
            const {
                roomView: { roomId },
                uploads,
            } = action.payload;
            return store.merge({
                uploadingByRoomId: updateUploading(store.uploadingByRoomId, roomId, old =>
                    old.merge(
                        uploads.map(
                            (file, tag): Uploading => ({
                                tag,
                                progress: 0,
                                file,
                                state: "uploading",
                            })
                        )
                    )
                ),
            });
        }
        case actionCreators.uploadFile.type: {
            const {
                roomView: { roomId },
                tag,
            } = action.payload;
            return store.merge({
                uploadingByRoomId: updateUploading(store.uploadingByRoomId, roomId, old => {
                    const { payload } = action;
                    switch (payload.status) {
                        case "progress": {
                            return mapUpdateExisting(old, tag, x => {
                                if (x.state === "uploading") {
                                    const { progress } = payload;
                                    return { ...x, progress };
                                }
                                return x;
                            });
                        }
                        case "cancel": {
                            return old.remove(tag);
                        }
                        case "error": {
                            return old.remove(tag);
                        }
                        case "done": {
                            return mapUpdateExisting(
                                old,
                                tag,
                                ({ file }): Uploading => ({
                                    tag,
                                    file,
                                    state: "uploaded",
                                    data: payload.response,
                                })
                            );
                        }
                    }
                }),
            });
        }
        case actionCreators.onRoomText.type: {
            const { data } = action.payload;
            // remove file uploads that were sent; FIXME: do that when sending text, not after it was sent
            if (data.shared_files && data.shared_files.length) {
                const sharedFileIds = I.Set(data.shared_files.map(x => x.id));
                return store.merge({
                    uploadingByRoomId: filterUploading(store.uploadingByRoomId, x => {
                        return !(x.state === "uploaded" && sharedFileIds.contains(x.data.id));
                    }),
                });
            }
            return store;
        }

        default: {
            return store;
        }
    }
}

function updateUploading(
    uploadingByRoomId: Store["uploadingByRoomId"],
    roomId: string,
    updater: (old: UploadingByTag) => UploadingByTag
) {
    return uploadingByRoomId.update(roomId, I.Map<tag, Uploading>(), updater);
}

function filterUploading(
    uploadingByRoomId: Store["uploadingByRoomId"],
    filter: (old: Uploading) => boolean
) {
    return uploadingByRoomId.map(old => old.filter(filter));
}

export type OuterStore = { uploads: Store };

export function getUploading(store: OuterStore, { roomId }: RoomView) {
    return store.uploads.uploadingByRoomId.get(roomId, I.Map<tag, Uploading>()).toList();
}

export const getUploaded = createSelector(
    (store: OuterStore) => store.uploads.uploadingByRoomId,
    byOpenRoom =>
        byOpenRoom.map(x =>
            x
                .toList()
                .map(x => (x.state === "uploaded" ? x.data : undefined))
                .filter(notUndefined)
        )
);
